<template>
    <form :class="classes.searchForm.form" role="search" @submit.prevent="onSubmit">
        <label :for="inputId" :class="classes.searchForm.label">{{ translate('agent-search.search.label') }}</label>
        <input
            :id="inputId"
            ref="input"
            v-model="value"
            type="search"
            name="q"
            :class="classes.searchForm.input"
            :placeholder="translate('agent-search.search.placeholder')"
            autocomplete="off"
            enterkeyhint="search"
            maxlength="200"
            :aria-invalid="empty ? 'true' : undefined"
            :aria-describedby="empty ? errorId : undefined"
            @input="empty = false"
            @keydown.enter="onEnter"
        />
        <button type="submit" :class="classes.searchForm.submit">{{ translate('agent-search.search.submit') }}</button>
        <div v-if="empty" :id="errorId" :class="classes.searchForm.error" role="alert">{{ translate('agent-search.search.empty') }}</div>
    </form>
</template>

<script setup lang="ts">
import { ref, useId, watch } from 'vue'
import { useTranslate } from './composables/useTranslate'
import { useClasses } from './composables/useClasses'

const props = defineProps<{
    query: string
}>()

const emit = defineEmits<{
    submit: [query: string]
}>()

const translate = useTranslate()
const classes = useClasses()
const inputId = useId()
const errorId = `${inputId}-error`
const value = ref(props.query)
const input = ref<HTMLInputElement | null>(null)
/** An empty search was submitted; cleared as soon as the user types. */
const empty = ref(false)

watch(
    () => props.query,
    (query) => {
        value.value = query
    },
)

/** Submits explicitly, so Enter works even where a host page suppresses implicit form submission. */
function onEnter(event: KeyboardEvent): void {
    // Enter confirms an IME composition (e.g. Japanese input) and must not submit then
    if (event.isComposing) {
        return
    }

    event.preventDefault()
    onSubmit()
}

function onSubmit(): void {
    const query = value.value.trim()

    if (!query) {
        empty.value = true
        input.value?.focus()

        return
    }

    empty.value = false
    emit('submit', query)
}
</script>
